import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TripList } from '../redux/trip/trip.types';

const STORAGE_KEY = 'selectedLists';

interface UseTripMapFiltersOptions {
  availableLists: TripList[];
}

interface UseTripMapFiltersReturn {
  selectedLists: TripList[];
  handleListToggle: (listId: string) => void;
  handleSelectAll: () => void;
  handleUnselectAll: () => void;
  isInitialized: boolean;
}

/**
 * useTripMapFilters - Custom hook for managing trip map filter state with sessionStorage persistence
 *
 * Handles initialization from sessionStorage, user selections, and synchronization with fresh list data.
 * Follows the codebase pattern of extracting stateful logic into reusable hooks.
 */
function useTripMapFilters({ availableLists }: UseTripMapFiltersOptions): UseTripMapFiltersReturn {
  const [selectedLists, setSelectedLists] = useState<TripList[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const previousSelectedIdsRef = useRef<string[]>([]);
  // Tracks every list ID ever seen — used to detect newly added lists and auto-select them
  const knownListIdsRef = useRef<Set<string>>(new Set());

  // Initialize from sessionStorage and sync with fresh data when available lists change
  useEffect(() => {
    if (availableLists.length === 0) {
      return;
    }

    if (!isInitialized) {
      // Initial load - read from sessionStorage
      const storedValue = sessionStorage.getItem(STORAGE_KEY);

      let initialLists: TripList[];
      if (!storedValue) {
        initialLists = availableLists;
      } else {
        try {
          const storedListIds = JSON.parse(storedValue).map((list: TripList) => list.id);
          initialLists = availableLists.filter((list) => storedListIds.includes(list.id));
        } catch {
          initialLists = availableLists;
        }
      }
      availableLists.forEach((l) => knownListIdsRef.current.add(l.id));
      setSelectedLists(initialLists);
      previousSelectedIdsRef.current = initialLists.map((l) => l.id).sort();
      setIsInitialized(true);
    } else {
      // After initialization - sync with fresh data while preserving user preferences.
      // Any list ID not in knownListIdsRef is newly added and defaults to selected.
      const storedValue = sessionStorage.getItem(STORAGE_KEY);
      const storedListIds = new Set<string>(
        storedValue ? JSON.parse(storedValue).map((list: TripList) => list.id) : []
      );

      const updatedLists = availableLists.filter(
        (list) => storedListIds.has(list.id) || !knownListIdsRef.current.has(list.id)
      );
      const updatedIds = updatedLists.map((l) => l.id).sort();

      availableLists.forEach((l) => knownListIdsRef.current.add(l.id));

      if (!isEqual(previousSelectedIdsRef.current, updatedIds)) {
        setSelectedLists(updatedLists);
        previousSelectedIdsRef.current = updatedIds;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedLists));
      }
    }
  }, [availableLists, isInitialized]);

  // Save to sessionStorage helper
  const saveToStorage = useCallback((lists: TripList[]) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(lists));
  }, []);

  const handleListToggle = useCallback(
    (listId: string) => {
      setSelectedLists((current) => {
        const isSelected = current.some((list) => list.id === listId);
        const newSelection = isSelected
          ? current.filter((list) => list.id !== listId)
          : [...current, ...availableLists.filter((list) => list.id === listId)];

        previousSelectedIdsRef.current = newSelection.map((l) => l.id).sort();
        saveToStorage(newSelection);
        return newSelection;
      });
    },
    [availableLists, saveToStorage]
  );

  const handleSelectAll = useCallback(() => {
    setSelectedLists(availableLists);
    previousSelectedIdsRef.current = availableLists.map((l) => l.id).sort();
    saveToStorage(availableLists);
  }, [availableLists, saveToStorage]);

  const handleUnselectAll = useCallback(() => {
    setSelectedLists([]);
    previousSelectedIdsRef.current = [];
    saveToStorage([]);
  }, [saveToStorage]);

  return {
    selectedLists,
    handleListToggle,
    handleSelectAll,
    handleUnselectAll,
    isInitialized,
  };
}

export default useTripMapFilters;
